/**
 * MODULE: _shared/frontend/lib/frontend/utils/assistantContextActionHandlers.ts
 * PURPOSE: Route assistant context actions to CRM navigation, compose draft, or chat prompts [2.2.1248].
 * PUBLIC_API: resolveAssistantContextAction, isNavigateAssistantAction, isComposeDraftAction, assistantEntityPath
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-05-26 [2.2.1254] — compose_draft opens CRM email compose with check-in prefill.
 */

import type { AssistantContextAction } from '../api/assistantContextApi';

export type AssistantActionExecution =
  | { kind: 'navigate'; path: string; label: string }
  /** Auto-send to assistant (advisor chips — summarize activity). */
  | { kind: 'send_prompt'; text: string }
  /** Prefill only — Pages editor note path or manual send. */
  | { kind: 'prompt'; text: string }
  /** Open Sales Hub golden-path email template create modal (Assistant page). */
  | { kind: 'open_email_template_create'; label: string }
  /** Open generative quota planner modal (My quota / assistant). */
  | { kind: 'open_sales_target_set'; label: string };

const OPEN_SALES_TARGET_SET_ACTIONS = new Set([
  'set_sales_quota',
  'open_sales_target_set',
  'set_my_quota',
]);

const NAVIGATE_ACTIONS = new Set([
  'view_in_crm',
  'navigate_entity',
  'open_in_crm',
  'open_deal',
  'open_contact',
  'open_account',
]);

const ADVISOR_EXECUTE_ACTIONS = new Set([
  'summarize_activity',
  'log_call',
  'schedule_executive_call',
  'advisor_execute',
  'advisor_query',
]);

const COMPOSE_DRAFT_ACTIONS = new Set([
  'draft_check_in_email',
  'draft_email',
  'compose_draft',
]);

const OPEN_EMAIL_TEMPLATE_CREATE_ACTIONS = new Set([
  'create_email_template',
  'open_email_template_create',
]);

const ADVISOR_EXECUTE_LABEL_RE =
  /\b(summarize|log a call|schedule executive|follow up)\b/i;

const COMPOSE_DRAFT_LABEL_RE = /\b(draft|check-in email|check in email)\b/i;

type EntityLinkInput = {
  entity_type?: string;
  entity_id?: string;
  metadata?: Record<string, unknown>;
};

/** CRM deep links shared by Assistant and Pages brain panels. */
export function assistantEntityPath(entity: EntityLinkInput): string | null {
  const type = (entity.entity_type || '').toLowerCase();
  const id = (entity.entity_id || '').trim();
  if (!id) return null;
  if (type === 'page') {
    const workspaceId = entity.metadata?.workspace_id;
    if (typeof workspaceId === 'string' && workspaceId.trim()) {
      return `/workspaces/${encodeURIComponent(workspaceId.trim())}/pages/${encodeURIComponent(id)}`;
    }
    return null;
  }
  if (type === 'deal' || type === 'opportunity') return `/crm/deals/${encodeURIComponent(id)}`;
  if (type === 'contact') return `/crm/contacts/${encodeURIComponent(id)}`;
  if (type === 'account' || type === 'company') return `/crm/accounts?accountId=${encodeURIComponent(id)}`;
  return null;
}

/** Advisor draft email — opens CRM compose with entity context [2.2.1254]. */
export function assistantComposeDraftPath(entity: EntityLinkInput): string | null {
  const base = assistantEntityPath(entity);
  if (!base) return null;
  const params = new URLSearchParams({ composeEmail: '1', draftHint: 'check-in' });
  const join = base.includes('?') ? '&' : '?';
  return `${base}${join}${params.toString()}`;
}

export function isNavigateAssistantAction(action: AssistantContextAction): boolean {
  if ((action.execution_mode || '').toLowerCase() === 'navigate') {
    return Boolean(action.entity_type && action.entity_id);
  }
  const code = (action.action || '').trim().toLowerCase();
  if (code && NAVIGATE_ACTIONS.has(code)) {
    return Boolean(action.entity_type && action.entity_id);
  }
  const label = (action.label || '').trim().toLowerCase();
  if (
    label.startsWith('view in crm') ||
    label.startsWith('open in crm') ||
    label.startsWith('view deal') ||
    label.startsWith('view contact') ||
    label.startsWith('view account')
  ) {
    return Boolean(action.entity_type && action.entity_id);
  }
  return false;
}

export function isOpenSalesTargetSetAction(action: AssistantContextAction): boolean {
  const mode = (action.execution_mode || '').toLowerCase();
  if (mode === 'open_sales_target_set') return true;
  const code = (action.action || '').trim().toLowerCase();
  return Boolean(code && OPEN_SALES_TARGET_SET_ACTIONS.has(code));
}

export function isOpenEmailTemplateCreateAction(action: AssistantContextAction): boolean {
  const mode = (action.execution_mode || '').toLowerCase();
  if (mode === 'open_email_template_create') return true;
  const code = (action.action || '').trim().toLowerCase();
  return Boolean(code && OPEN_EMAIL_TEMPLATE_CREATE_ACTIONS.has(code));
}

export function isComposeDraftAction(action: AssistantContextAction): boolean {
  const mode = (action.execution_mode || '').toLowerCase();
  if (mode === 'compose_draft') {
    return Boolean(action.entity_type && action.entity_id);
  }
  const code = (action.action || '').trim().toLowerCase();
  if (code && COMPOSE_DRAFT_ACTIONS.has(code)) {
    return Boolean(action.entity_type && action.entity_id);
  }
  const label = (action.label || '').trim();
  return Boolean(label && COMPOSE_DRAFT_LABEL_RE.test(label) && action.entity_type && action.entity_id);
}

export function isAdvisorExecuteAction(action: AssistantContextAction): boolean {
  const mode = (action.execution_mode || '').toLowerCase();
  if (
    mode === 'prompt' ||
    mode === 'navigate' ||
    mode === 'compose_draft' ||
    mode === 'open_email_template_create' ||
    mode === 'open_sales_target_set'
  ) {
    return false;
  }
  if (mode === 'advisor_execute') {
    return Boolean((action.label || action.action || '').trim());
  }
  if (isComposeDraftAction(action)) return false;
  const code = (action.action || '').trim().toLowerCase();
  if (code && ADVISOR_EXECUTE_ACTIONS.has(code)) {
    return true;
  }
  const label = (action.label || '').trim();
  return Boolean(label && ADVISOR_EXECUTE_LABEL_RE.test(label));
}

export function isPromptAssistantAction(action: AssistantContextAction): boolean {
  const mode = (action.execution_mode || '').toLowerCase();
  if (mode === 'prompt') {
    return Boolean((action.label || action.action || '').trim());
  }
  if (
    isNavigateAssistantAction(action) ||
    isComposeDraftAction(action) ||
    isOpenEmailTemplateCreateAction(action) ||
    isOpenSalesTargetSetAction(action) ||
    isAdvisorExecuteAction(action)
  ) {
    return false;
  }
  return Boolean((action.label || action.action || '').trim());
}

export function resolveAssistantContextAction(action: AssistantContextAction): AssistantActionExecution {
  const label = (action.label || '').trim();
  if (isOpenEmailTemplateCreateAction(action)) {
    return { kind: 'open_email_template_create', label: label || 'Create email template' };
  }
  if (isOpenSalesTargetSetAction(action)) {
    return { kind: 'open_sales_target_set', label: label || 'Set your quota' };
  }
  if (isNavigateAssistantAction(action)) {
    const path = assistantEntityPath({
      entity_type: action.entity_type,
      entity_id: action.entity_id,
    });
    if (path) {
      return { kind: 'navigate', path, label: label || 'View in CRM' };
    }
  }
  if (isComposeDraftAction(action)) {
    const path = assistantComposeDraftPath({
      entity_type: action.entity_type,
      entity_id: action.entity_id,
    });
    if (path) {
      return { kind: 'navigate', path, label: label || 'Draft email' };
    }
  }
  const promptText = (action.prompt_text || '').trim();
  const labelOrAction = (action.label || action.action || '').trim();
  // Golden-path email template chips carry grounded NL in prompt_text (not display label).
  if ((action.source || '') === 'email_template_snapshot' && promptText) {
    return { kind: 'send_prompt', text: promptText };
  }
  if (isAdvisorExecuteAction(action) && labelOrAction) {
    return { kind: 'send_prompt', text: labelOrAction };
  }
  return { kind: 'prompt', text: labelOrAction };
}

/** Prompt text for send/prompt execution kinds; null for navigate/modal actions. */
export function assistantActionPromptText(resolved: AssistantActionExecution): string | null {
  if (resolved.kind === 'send_prompt' || resolved.kind === 'prompt') {
    const text = resolved.text.trim();
    return text || null;
  }
  return null;
}
